import {
  selectSeance,
  selectSeanceAdvice,
  selectSeanceMateriels,
} from "domain/seances/seances.selectors";
import {
  HiArrowLeft,
  HiDotsVertical,
  HiHeart,
  HiOutlineClock,
  HiOutlineDuplicate,
  HiOutlineFolderAdd,
  HiOutlineHeart,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import {
  getDurationInHours,
  getTotalSeanceDuration,
} from "ui/utils/formatters";
import { SeanceBanner } from "ui/pages/app/seances/seance/SeanceBanner";
import { TechniqueComponent } from "ui/pages/app/seances/seance/TechniqueComponent";
import { useWindowWidth } from "ui/utils/hooks";
import { Advice } from "ui/pages/app/components/activities/Advice";
import { Note } from "ui/pages/app/components/activities/Note";
import { Materiels } from "ui/pages/app/components/activities/Materiels";
import { selectIsPremium, selectUserName } from "domain/auth/auth.selectors";
import { selectFavoriteSeances } from "domain/activities/activities.selectors";
import { toggleActivityFavorite } from "ui/utils/toggle-activity-favorite";
import { Trans } from "react-i18next";
import {
  deleteSeance,
  duplicateSeance,
  getSeance,
  getSeanceAdvice,
  getSeanceMateriels,
  getUserSeanceImages,
  removeSeanceAdvice,
  removeSeanceTiming,
} from "domain/seances/seances.actions";
import { Snackbar } from "ui/pages/snackbar";
import { ModalDeleteRessource } from "ui/pages/app/components/activities/ModalDeleteRessource";
import { useDisclosure } from "@chakra-ui/hooks";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { SeancePDF } from "ui/pages/app/seances/seance/SeancePDF";
import { useEffect, useState } from "react";
import { PopoverContent, PopoverTrigger } from "@chakra-ui/react";
import { Popover } from "@chakra-ui/react";
import { ModalChooseFolder } from "ui/pages/app/my-creations/components/ModalChooseFolder";
import { DownloadActivityTooltip } from "ui/pages/app/components/activities/DownloadActivityTooltip";
import { i18nKey } from "domain/entities/activities.entities";
import { DownloadImageIcon } from "ui/assets/svg/icons/DownloadImageIcon";
import { PDFIcon } from "ui/assets/svg/icons/PDFIcon";
import { getDownloadLink } from "domain/activities/activities.actions";
import { ModalManageTiming } from "ui/pages/app/seances/seance/ModalManageTiming";

export function Seance({ selectedSeanceId }: { selectedSeanceId: string }) {
  const seance = useAppSelector(selectSeance);
  const advice = useAppSelector(selectSeanceAdvice);
  const materiels = useAppSelector(selectSeanceMateriels);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isDesktop = useWindowWidth();
  const modalDeleteState = useDisclosure();
  const modalManageTimingState = useDisclosure();
  const userName = useAppSelector(selectUserName);
  const favoriteSeance = useAppSelector(selectFavoriteSeances).find(
    (t) => t.seanceId === seance?.id
  );
  const isPremium = useAppSelector(selectIsPremium);
  const [isLoading, setIsLoading] = useState(true);
  const [startTimes, setStartTimes] = useState<string[]>([]);
  const popoverState = useDisclosure();
  const timingPopoverState = useDisclosure();
  const modalChooseFolderState = useDisclosure();

  useEffect(() => {
    if (selectedSeanceId) {
      fetchInfos();
    }
  }, [selectedSeanceId]); //eslint-disable-line

  useEffect(() => {
    if (seance?.techniques) {
      const startTimesTemp: string[] = [];
      if (seance.timing) {
        const [h, m] = seance.timing.split(":");
        let minutes = Number(h) * 60 + Number(m);
        for (const technique of seance.techniques) {
          startTimesTemp.push(
            `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(
              minutes % 60
            ).padStart(2, "0")}`
          );
          minutes += Number(technique.duration);
        }
      }
      setStartTimes(startTimesTemp);
    }
  }, [seance?.timing]); //eslint-disable-line

  const fetchInfos = async () => {
    const { seance, success } = await dispatch(
      getSeance(Number(selectedSeanceId))
    ).unwrap();
    if (success) {
      if (seance?.premium && !isPremium) {
        navigate("/seances");
      }
      if (!seance.userId) {
        await dispatch(getSeanceAdvice(Number(selectedSeanceId)));
      } else await dispatch(removeSeanceAdvice());
      await dispatch(getSeanceMateriels(Number(selectedSeanceId)));
    } else navigate("/seances");
    setIsLoading(false);
  };

  const handleFavoriteClick = async (e: any) => {
    e.stopPropagation();
    toggleActivityFavorite({
      favoriteActivity: favoriteSeance,
      dispatch,
      activityId: seance.id,
      activityType: "seance",
    });
  };

  const handleDuplication = async () => {
    const result = await dispatch(duplicateSeance(seance.id)).unwrap();
    if (result.success) {
      Snackbar.showSuccess({
        title: translate("ns1:Seance.Duplicated"),
      });
    }
  };

  const handleDeleteSeance = async () => {
    const result = await dispatch(deleteSeance(seance.id)).unwrap();
    if (result.success) {
      Snackbar.showSuccess({
        title: translate("ns1:Activities.RessourceDeleted"),
      });
      navigate("/my-creations");
    }
  };

  const handleEditSeance = async () => {
    if (!seance?.userId) {
      const { seanceId } = await dispatch(duplicateSeance(seance.id)).unwrap();
      if (seanceId) {
        navigate("/create-seance", { state: { seanceId } });
      }
    } else navigate("/create-seance", { state: { seanceId: seance.id } });
  };

  const handleDownloadClick = async (e: any) => {
    e.stopPropagation();
    if (!isPremium) {
      await Snackbar.removeDownloadActivityAlert();
      Snackbar.showDownloadActivityAlert(() =>
        navigate("/account", { replace: true })
      );
    }
  };

  const handleFolderClick = (e: any) => {
    e.stopPropagation();
    modalChooseFolderState.onOpen();
  };

  const handleCloseChooseFolder = () => {
    modalChooseFolderState.onClose();
    setTimeout(() => {
      fetchInfos();
    }, 200);
  };

  const handleDownloadPDFClick = (e: any) => {
    e.stopPropagation();
    Snackbar.showSuccess({
      title: translate("ns1:Seance.DownloadPDFSuccess"),
      variant: "secondary",
    });
  };

  const handleDownloadImagesClick = async (e: any) => {
    e.stopPropagation();
    if (seance.isInniz) {
      const result = await dispatch(
        getDownloadLink({
          type: "whiteboard",
          id: `seances/${seance.innizRef}.zip`,
        })
      ).unwrap();
      if (result.success && result.url) {
        window.open(result.url, "_blank");
        Snackbar.showSuccess({
          title: translate("ns1:Seance.DownloadVisualsSuccess"),
          variant: "secondary",
        });
      }
    } else {
      Snackbar.showSuccess({
        title: translate("ns1:Activities.Tooltips.LoadingDownloadImages"),
        variant: "secondary",
        type: "download",
      });
      const result = await dispatch(getUserSeanceImages(seance.id)).unwrap();
      if (result.success && result.url) {
        window.open(result.url, "_blank");
        Snackbar.showSuccess({
          title: translate("ns1:Seance.DownloadVisualsSuccess"),
          variant: "secondary",
        });
      }
    }
  };

  const handleTimingClick = (e: any) => {
    e.stopPropagation();
    modalManageTimingState.onOpen();
  };

  const handleRemoveTimingClick = async (e: any) => {
    e.stopPropagation();
    if (seance.id) {
      const result = await dispatch(removeSeanceTiming(seance?.id)).unwrap();
      if (result.success) {
        Snackbar.showSuccess({
          title: translate("ns1:Seance.TimingRemoved"),
        });
      }
    }
    timingPopoverState.onClose();
  };

  return (
    <div className="flex h-full flex-col p-4 md:flex-row md:py-8 md:pl-6 md:pr-10">
      <div className="mb-4 flex w-full items-center justify-between md:my-0 md:w-12 md:items-start">
        <button
          onClick={() => navigate(-1)}
          className="z-50 flex h-5 items-center"
        >
          <HiArrowLeft size={24} className="text-darkGrey-800" />
        </button>
        {!isDesktop && (
          <div className="flex w-fit space-x-4">
            <Popover
              isOpen={timingPopoverState.isOpen}
              onOpen={seance?.timing ? timingPopoverState.onOpen : undefined}
              onClose={timingPopoverState.onClose}
              placement="bottom"
            >
              <PopoverTrigger>
                <button
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    seance?.timing
                      ? timingPopoverState.isOpen
                        ? "bg-darkGrey-200"
                        : "bg-darkGrey-800"
                      : "bg-white"
                  }`}
                  onClick={seance?.timing ? undefined : handleTimingClick}
                >
                  <HiOutlineClock
                    className={`${
                      seance?.timing && !timingPopoverState.isOpen
                        ? "text-white"
                        : "text-secondaryGrey-900"
                    }`}
                    size={20}
                  />
                </button>
              </PopoverTrigger>
              {timingPopoverState.isOpen && (
                <PopoverContent
                  style={{
                    isolation: "isolate",
                    zIndex: 100,
                    width: "fit-content",
                    border: "none",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                    <button
                      className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                      onClick={handleTimingClick}
                    >
                      <HiOutlineClock
                        className="me-2 text-secondaryGrey-900"
                        size={20}
                      />
                      {translate("ns1:Seance.UpdateTimingStart")}
                    </button>
                    <button
                      className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                      onClick={handleRemoveTimingClick}
                    >
                      <HiOutlineX
                        className="me-2 text-secondaryGrey-900"
                        size={20}
                      />
                      {translate("ns1:Seance.RemoveTiming")}
                    </button>
                  </div>
                </PopoverContent>
              )}
            </Popover>
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={handleFavoriteClick}
            >
              {!!favoriteSeance?.id ? (
                <HiHeart className="text-secondaryGrey-900" size={20} />
              ) : (
                <HiOutlineHeart className="text-secondaryGrey-900" size={20} />
              )}
            </button>

            <Popover
              isOpen={popoverState.isOpen}
              onOpen={popoverState.onOpen}
              onClose={popoverState.onClose}
              placement="right-start"
            >
              <DownloadActivityTooltip type="seance" mobile={!isDesktop}>
                <button
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    popoverState.isOpen ? "bg-darkGrey-200" : "bg-white"
                  }`}
                >
                  <HiDotsVertical
                    className={`${
                      popoverState.isOpen
                        ? "text-darkGrey-700"
                        : "text-secondaryGrey-900"
                    }`}
                    size={20}
                  />
                </button>
              </DownloadActivityTooltip>
              {popoverState.isOpen && (
                <PopoverContent
                  style={{
                    isolation: "isolate",
                    zIndex: 100,
                    width: "fit-content",
                    border: "none",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                    {seance?.userId && (
                      <button
                        className={`flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100 ${
                          !seance.active ? "opacity-30" : ""
                        }`}
                        disabled={!seance.active}
                        onClick={handleFolderClick}
                      >
                        <HiOutlineFolderAdd
                          className="me-2 text-secondaryGrey-900"
                          size={20}
                        />
                        {translate("ns1:Button.AddToFolder")}
                      </button>
                    )}
                    {seance && (
                      <button
                        className={`flex w-full rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                        onClick={handleDownloadImagesClick}
                      >
                        <span className="me-2">
                          <DownloadImageIcon color="#2b3674" />
                        </span>
                        {translate("ns1:Seance.DownloadImages")}
                      </button>
                    )}
                    {isPremium && seance ? (
                      <PDFDownloadLink
                        document={
                          <SeancePDF
                            seance={seance}
                            advice={advice}
                            materiels={materiels}
                            userName={userName}
                            startTimes={startTimes}
                          />
                        }
                        fileName={`${seance.title}.pdf`}
                      >
                        <button
                          className={`flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                          onClick={handleDownloadPDFClick}
                        >
                          <PDFIcon
                            className="me-2 text-secondaryGrey-900"
                            size={20}
                          />
                          {translate("ns1:Button.Download")}
                        </button>
                      </PDFDownloadLink>
                    ) : (
                      <button
                        className={`flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                        onClick={handleDownloadClick}
                      >
                        <PDFIcon
                          className="me-2 text-secondaryGrey-900"
                          size={20}
                        />
                        {translate("ns1:Button.Download")}
                      </button>
                    )}

                    {isPremium && (
                      <button
                        className={`flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100`}
                        onClick={handleEditSeance}
                      >
                        <HiOutlinePencil
                          className="me-2 text-secondaryGrey-900"
                          size={20}
                        />
                        {translate("ns1:Button.Edit")}
                      </button>
                    )}
                    {seance?.userId && (
                      <>
                        <button
                          className={`flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100 ${
                            !seance.active ? "opacity-30" : ""
                          }`}
                          disabled={!seance.active}
                          onClick={handleDuplication}
                        >
                          <HiOutlineDuplicate
                            className="me-2 text-secondaryGrey-900"
                            size={20}
                          />
                          {translate("ns1:Button.Duplicate")}
                        </button>
                        <button
                          className="flex rounded-[10px] px-3 py-2 text-error hover:bg-darkGrey-100"
                          onClick={handleDeleteSeance}
                        >
                          <HiOutlineTrash
                            className="me-2 text-error"
                            size={20}
                          />
                          {translate("ns1:Button.Delete")}
                        </button>
                      </>
                    )}
                  </div>
                </PopoverContent>
              )}
            </Popover>
          </div>
        )}
      </div>

      {seance && !isLoading ? (
        <>
          {" "}
          <div className="flex w-full flex-col space-y-6 md:space-y-8">
            <SeanceBanner
              seance={seance}
              isFavorite={!!favoriteSeance?.id}
              handleFavoriteClick={handleFavoriteClick}
              handleDuplication={handleDuplication}
              openDeleteModal={() => modalDeleteState.onOpen()}
              handleEditSeance={handleEditSeance}
              handleDownloadClick={handleDownloadClick}
              handleFolderClick={handleFolderClick}
              handleDownloadPDFClick={handleDownloadPDFClick}
              handleDownloadImagesClick={handleDownloadImagesClick}
              handleTimingClick={handleTimingClick}
              handleRemoveTimingClick={handleRemoveTimingClick}
              timingPopoverState={timingPopoverState}
              startTimes={startTimes}
            />

            <div className="flex flex-wrap gap-x-3 gap-y-3 md:justify-start">
              {seance?.userId && (
                <div className="flex flex h-7 w-7 items-center items-center justify-center rounded-full bg-darkGrey-700 text-xs font-semibold text-white">
                  {userName?.charAt(0)}
                </div>
              )}
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-700 px-3 text-xs font-semibold text-white">
                {getDurationInHours(getTotalSeanceDuration(seance))}
              </div>
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                {seance?.onsite &&
                  seance?.remote &&
                  translate("ns1:Activities.remote&onsite")}
                {seance?.onsite &&
                  !seance?.remote &&
                  translate("ns1:Activities.onsite")}
                {!seance?.onsite &&
                  seance?.remote &&
                  translate("ns1:Activities.remote")}
              </div>
              {seance.participants ? (
                <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                  <Trans
                    i18nKey={"Activities.UntilParticipants" as i18nKey}
                    values={{ participants: seance.participants }}
                  />
                </div>
              ) : (
                <></>
              )}
              {seance.participants === 0 && (
                <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                  <Trans
                    i18nKey={"Activities.MoreThanParticipants" as i18nKey}
                    values={{ participants: 20 }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col space-x-0 space-y-6 md:flex-row md:space-x-8 md:space-y-0">
              <div className="flex w-full flex-col space-y-6 md:space-y-8">
                <div className="rounded-[30px] bg-white p-4 py-6 md:p-6">
                  <span className="text-base font-medium md:text-sm">
                    {`${translate("ns1:Activities.Goal")}: ${seance?.objectif}`}
                  </span>
                  <span className="mt-4 block text-sm font-medium">
                    {translate("ns1:Activities.ActivitySteps")}
                  </span>
                  {seance?.techniques.length > 0 &&
                    seance?.techniques.map((technique, index) => (
                      <TechniqueComponent
                        key={"technique" + index}
                        technique={technique}
                        index={index}
                        techniquesLength={seance?.techniques.length}
                        startTime={startTimes[index]}
                      />
                    ))}
                </div>

                {/* advice and equipment for mobile */}
                {!isDesktop &&
                  (advice || seance?.note || materiels.length > 0) && (
                    <div className="flex flex-col space-y-6 md:space-y-8">
                      {seance?.note && <Note note={seance?.note} />}
                      {materiels?.length > 0 && (
                        <Materiels materiels={materiels} />
                      )}
                      {advice && <Advice advice={advice} />}
                    </div>
                  )}
              </div>

              {/* advice and equipment for desktop */}
              {isDesktop &&
                (advice || seance?.note || materiels.length > 0) && (
                  <div className="flex w-80 flex-col space-y-6 md:space-y-8">
                    {seance?.note && <Note note={seance?.note} />}
                    {materiels?.length > 0 && (
                      <Materiels materiels={materiels} />
                    )}
                    {advice && <Advice advice={advice} />}
                  </div>
                )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <div className="loader" />
        </div>
      )}

      {!!modalDeleteState.isOpen && (
        <ModalDeleteRessource
          isOpen={modalDeleteState.isOpen}
          onClose={modalDeleteState.onClose}
          deleteRessource={handleDeleteSeance}
        />
      )}

      {!!modalChooseFolderState.isOpen && seance && (
        <ModalChooseFolder
          isOpen={modalChooseFolderState.isOpen}
          onClose={handleCloseChooseFolder}
          seanceId={seance.id}
          seanceFolders={seance.folders}
        />
      )}

      {!!modalManageTimingState.isOpen && seance?.id && (
        <ModalManageTiming
          isOpen={modalManageTimingState.isOpen}
          onClose={modalManageTimingState.onClose}
          timing={seance?.timing || ""}
          seanceId={seance.id}
        />
      )}
    </div>
  );
}
